import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * GuestWelcomeHero — J1 landing hero (#1921, copy round 2 #2098).
 *
 * Renders ONLY on the default (non-audience, non-partner-branded) homepage
 * — `HomePageV2.jsx` gates this on `!audienceSlug`, since `/for/:audience`
 * pages carry their own partner-specific hero copy/branding that this
 * generic welcome must never override.
 *
 * #2098 calls implemented here:
 *   1. Landing wordiness — the three `.gd-hero-points` bullets (73 words)
 *      cut to the single line "No account needed." (30 words), decided
 *      2026-08-12: all three points are restated two taps later on the
 *      commitment sheet, where the modality toggle actually is.
 *   3. Headline — "Practise the hard part of the job." (decided 2026-08-13,
 *      replacing "Rehearse the conversation you're dreading"): names the
 *      work rather than diagnosing the reader, true of all 152 scenarios
 *      today, work-framed without promising the pathway-level engagement
 *      arc the product doesn't build here. Subtitle UNCHANGED per the
 *      decision (it locks with the new headline — "the hard part" carries
 *      across both lines).
 *   5. Browse-all — the secondary CTA is deleted (measured 2026-08-08:
 *      zero scenarios reachable only through it; the addendum also hides
 *      the `#scenarios` gallery this anchor pointed at — see
 *      `HomePageV2.jsx`'s `!audienceSlug` gate on that section).
 *
 * Sign-in prominence (#2098 call 2) moved OUT of this component entirely —
 * it now lives in the new persistent top-right `GuestHomeHeader`
 * (`HomePageV2.jsx`), replacing the borderless `.gd-hero-signin` link that
 * used to sit below the CTAs here.
 *
 * The privacy line says only what is true ("No account needed") and does
 * NOT claim the conversation isn't stored — it is, because that's what the
 * feedback is generated from.
 *
 * Pure presentational — `onFindConversation` is the primary CTA's analytics
 * hook (container's job per #1930 Scope C: `discovery_cta_tapped`). No
 * fetching, no analytics state here.
 *
 * #2090: the eyebrow line used to hardcode the app's default English brand
 * name inline, unconditionally — a white-label hazard (Praxis/Umuzi is a
 * deliberate per-org `app_name`, see docs/subsystems/org-tenancy.md) and an
 * i18n gap. It now takes `brandName` as a prop (rather than reading the
 * branding context hook here directly) and reads the `hero.welcomeEyebrow`
 * translation key, joining the same `t()` pattern the rest of this hero's
 * sibling copy already uses in `HomePageV2.jsx`.
 * The prop indirection is deliberate, not stylistic: `BrandingContext`
 * imports `AuthContext`, which imports `../main.jsx` — and `main.jsx`
 * initialises `src/i18n.js`, whose module-level `document.documentElement`
 * write throws under this workspace's jsdom-less vitest environment (see
 * `reference_frontend_no_jsdom`, docs/subsystems/testing.md). Reading the
 * branding hook directly in this otherwise-pure component would have
 * dragged that chain into every test that renders it, including this
 * file's own `renderToStaticMarkup` test. The CALLER (`HomePageV2.jsx`)
 * already runs that hook to compute `brandName` for its own audience-page
 * hero branch — `audienceConfig` and `orgBranding` are always null on this
 * default, non-audience route, so its `appName` field is that
 * computation's exact result here too — so passing it down costs nothing
 * extra.
 */
function GuestWelcomeHero({ onFindConversation, brandName }) {
  const { t } = useTranslation();

  return (
    <div className="v2-hero-content">
      <p className="gd-hero-eyebrow v2-fade-up">
        {t('hero.welcomeEyebrow', { brandName })}
      </p>
      <h1 className="gd-hero-headline v2-fade-up v2-stagger-1">
        Practise the hard part of the job.
      </h1>
      <p className="v2-hero-subtitle v2-fade-up v2-stagger-2">
        An AI plays the other person. You practise saying the hard part out loud.
      </p>

      <p className="gd-hero-points v2-fade-up v2-stagger-2">
        <span aria-hidden="true">🔒</span>
        <span>No account needed.</span>
      </p>

      <div className="v2-hero-actions v2-fade-up v2-stagger-3">
        <Link
          to="/discover"
          className="v2-btn v2-btn-primary"
          data-testid="gd-hero-find-conversation"
          onClick={onFindConversation}
        >
          Find my conversation
        </Link>
      </div>
    </div>
  );
}

export default GuestWelcomeHero;
